// src/components/forms/DropdownSingle.tsx
import { FC, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { Option } from "./types";
import { CheckIcon, ChevronUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";


// 1. Update the Props interface constraint
interface Props<T = string | number | boolean> {
  label: string;
  options: Option<T>[];
  value: T | null;
  onChange: (val: T | null) => void;
  placeholder?: string;
  className?: string;
}

// 2. Update the Component generic constraint
export const DropdownSingle = <T extends string | number | boolean>({
  label,
  options,
  value,
  onChange,
  placeholder,
  className,
}: Props<T>) => {
  // ... rest of your code
  const [query, setQuery] = useState("");

  const filtered =
    query === ""
      ? options
      : options.filter((opt) =>
          opt.label.toLowerCase().includes(query.toLowerCase())
        );

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div className={`flex flex-col gap-2 mt-7 ${className}`}>
      <span className="text-sm font-bold uppercase text-slate-200/70 dark:text-indigo-200 ml-1">
        {label}
      </span>

      {/* ✅ SAFE VALUE HANDLING */}
      <Listbox value={value ?? null} onChange={onChange}>
        {({ open }) => (
          <div className="relative group">
            
            {/* Glow */}
            <div
              className={`absolute -inset-0.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-xl blur-[2px] transition duration-300 ${
                open ? "opacity-100" : "opacity-20 group-hover:opacity-40"
              }`}
            />

            {/* Button */}
            <Listbox.Button className="relative w-full min-h-[44px] rounded-xl bg-white dark:bg-zinc-900 px-4 py-2 flex items-center justify-between border border-zinc-200 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 focus:outline-none transition-all">
              <div className="flex flex-wrap gap-1.5">
                {selectedOption ? (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-xs font-bold rounded-md border border-indigo-200 dark:border-indigo-800">
                    {selectedOption.label}

                    {/* ✅ CLEAR FIX */}
                    <XMarkIcon
                      className="w-3.5 h-3.5 cursor-pointer hover:text-pink-500"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChange(null); // ✅ use null
                      }}
                    />
                  </span>
                ) : (
                  <span className="text-zinc-400">
                    {placeholder || "Select..."}
                  </span>
                )}
              </div>

              <ChevronUpDownIcon
                className={`w-5 h-5 transition-transform duration-200 ${
                  open ? "text-pink-500 rotate-180" : "text-indigo-400"
                }`}
              />
            </Listbox.Button>

            <Transition
              show={open}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Listbox.Options className="absolute mt-2 w-full bg-black dark:bg-zinc-900 shadow-2xl rounded-xl max-h-64 overflow-hidden z-50 border border-zinc-100 dark:border-zinc-800 focus:outline-none">

                {/* Search */}
                <div className="p-2 border-b border-zinc-100 dark:border-zinc-800 flex items-center gap-2 bg-zinc-50/50 dark:bg-zinc-800/50">
                  <MagnifyingGlassIcon className="w-4 h-4 text-zinc-400 ml-1" />
                  <input
                    type="text"
                    autoFocus
                    className="w-full bg-transparent border-none focus:ring-0 text-sm"
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>

                <div className="overflow-auto max-h-48 p-1">
                  {filtered.length > 0 ? (
                    filtered.map((opt) => {
                      const isSelected = value === opt.value;

                      return (
                        <Listbox.Option
                          key={String(opt.value)} // ✅ safer key
                          value={opt.value}
                          className={({ active }) => `
                            cursor-pointer px-4 py-2.5 mx-1 rounded-lg 
                            ${active ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white font-semibold" : ""}
                           ${isSelected ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold shadow-md" : ""}
                          `}
                        >
                          <div className="flex justify-between items-center">
                            <span className="truncate">{opt.label}</span>
                            {isSelected && (
                              <CheckIcon className="w-5 h-5 text-white" />
                            )}
                          </div>
                        </Listbox.Option>
                      );
                    })
                  ) : (
                    <div className="py-6 text-center text-sm text-zinc-500">
                      No results found
                    </div>
                  )}
                </div>
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  );
};